// YouTubePracticePlayer — renders the YouTube IFrame player and exposes the adapter.
// Mirror mode is applied via CSS transform (scaleX(-1)) at this layer; the IFrame itself
// cannot own it directly. The adapter boundary keeps practice controls decoupled from YT globals.

import React, { useEffect, useRef, useState } from 'react';
import type { VideoViewCrop } from './types';
import VideoCropViewport from './VideoCropViewport';
import type { VideoCropFitMode } from './VideoCropViewport';
import { createYouTubeAdapter, YTPlayerLike, PracticeRuntimeAdapter } from './services/youtubePlayerAdapter';

// ---- YouTube IFrame API loading (window-scoped, safe to call multiple times) ----

function ensureYouTubeApiLoaded(onReady: () => void): void {
  const w = window as Window & {
    YT?: { Player: unknown };
    onYouTubeIframeAPIReady?: () => void;
    _ytApiReadyQueue?: Array<() => void>;
    _ytApiLoading?: boolean;
  };

  if (w.YT?.Player) {
    onReady();
    return;
  }

  if (!w._ytApiReadyQueue) w._ytApiReadyQueue = [];
  w._ytApiReadyQueue.push(onReady);

  if (w._ytApiLoading) return;
  w._ytApiLoading = true;

  const prev = w.onYouTubeIframeAPIReady;
  w.onYouTubeIframeAPIReady = () => {
    (w._ytApiReadyQueue ?? []).forEach(cb => cb());
    w._ytApiReadyQueue = [];
    delete w._ytApiLoading;
    if (prev) prev();
  };

  const script = document.createElement('script');
  script.src = 'https://www.youtube.com/iframe_api';
  document.head.appendChild(script);
}

// ---- Component ----

export interface YouTubePracticePlayerProps {
  videoId: string;
  startTime?: number;
  fitMode?: VideoCropFitMode;
  isMirrored: boolean;
  viewCrop?: VideoViewCrop;
  allowAutoPlay?: boolean;
  /** Called once when the adapter is ready (player has loaded). */
  onAdapterReady: (adapter: PracticeRuntimeAdapter) => void;
  /** Called at ~100 ms intervals with current playback position and total duration. */
  onTimeUpdate: (currentTime: number, duration: number) => void;
  /** Called whenever the YouTube player state changes to playing (true) or non-playing (false). */
  onPlayStateChange: (isPlaying: boolean) => void;
  /** Called when the video plays through to its end. */
  onEnded?: () => void;
}

// YT.Player exposes destroy() to tear down the iframe and listeners.
interface YTPlayerWithDestroy extends YTPlayerLike {
  destroy?: () => void;
}

// YT_STATE.ENDED = 0 and YT_STATE.PLAYING = 1 per the IFrame API.
const YT_STATE_ENDED = 0;
const YT_STATE_PLAYING = 1;
const POLL_INTERVAL_MS = 100;
const QUIET_YOUTUBE_PLAYER_VARS = {
  controls: 0,
  disablekb: 1,
  cc_load_policy: 0,
  iv_load_policy: 3,
  fs: 0,
  rel: 0,
  playsinline: 1,
  modestbranding: 1,
} as const;

const YouTubePracticePlayer: React.FC<YouTubePracticePlayerProps> = ({
  videoId,
  startTime,
  fitMode = 'contain',
  isMirrored,
  viewCrop,
  allowAutoPlay = true,
  onAdapterReady,
  onTimeUpdate,
  onPlayStateChange,
  onEnded,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // YouTube draws its own big play button (and, at the end, suggested videos) inside its sealed
  // iframe. Until the video is actually playing, cover it with the video's thumbnail so the app's
  // own centred play/pause is the only one on screen.
  const [showStartCover, setShowStartCover] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const playerRef = useRef<YTPlayerWithDestroy | null>(null);

  // Stable refs for the prop callbacks so the effect closure never goes stale.
  const onAdapterReadyRef = useRef(onAdapterReady);
  const onTimeUpdateRef = useRef(onTimeUpdate);
  const onPlayStateChangeRef = useRef(onPlayStateChange);
  const onEndedRef = useRef(onEnded);
  onAdapterReadyRef.current = onAdapterReady;
  onTimeUpdateRef.current = onTimeUpdate;
  onPlayStateChangeRef.current = onPlayStateChange;
  onEndedRef.current = onEnded;

  useEffect(() => {
    let destroyed = false;

    setShowStartCover(true);

    ensureYouTubeApiLoaded(() => {
      if (destroyed || !containerRef.current) return;

      const w = window as any;
      // new YT.Player replaces the div with an <iframe>.
      const player: YTPlayerWithDestroy = new w.YT.Player(containerRef.current, {
        videoId,
        playerVars: {
          autoplay: allowAutoPlay ? 1 : 0,
          start: startTime ?? 0,
          ...QUIET_YOUTUBE_PLAYER_VARS,
        },
        events: {
          onReady: () => {
            if (destroyed) return;
            playerRef.current = player;
            const adapter = createYouTubeAdapter(player as YTPlayerLike);
            onAdapterReadyRef.current(adapter);
            pollRef.current = setInterval(() => {
              if (destroyed || !adapter.isReady()) return;
              onTimeUpdateRef.current(adapter.getCurrentTime(), adapter.getDuration());
            }, POLL_INTERVAL_MS);
          },
          onStateChange: (event: { data: number }) => {
            if (destroyed) return;
            onPlayStateChangeRef.current(event.data === YT_STATE_PLAYING);
            if (event.data === YT_STATE_PLAYING) setShowStartCover(false);
            if (event.data === YT_STATE_ENDED) {
              setShowStartCover(true);
              onEndedRef.current?.();
            }
          },
        },
      });
      playerRef.current = player;
    });

    return () => {
      destroyed = true;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      // Tear down the YT iframe and its listeners so a fresh mount can attach cleanly.
      const player = playerRef.current;
      if (player?.destroy) {
        try { player.destroy(); } catch { /* YT already gone */ }
      }
      playerRef.current = null;
    };
  // videoId, startTime, and autoplay mode drive the player instance.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, startTime, allowAutoPlay]);

  return (
    <VideoCropViewport
      aspectRatio={16 / 9}
      mediaKind="youtube"
      fitMode={fitMode}
      isMirrored={isMirrored}
      viewCrop={viewCrop}
    >
      <div className="youtube-iframe-shell">
        {/* The YT.Player constructor replaces this div with the IFrame element. */}
        <div ref={containerRef} className="youtube-iframe-host" />
        {showStartCover && (
          <img
            className="youtube-start-cover"
            src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
            alt=""
            aria-hidden="true"
            data-testid="youtube-start-cover"
            onError={(event) => {
              // No thumbnail: a plain black cover still hides YouTube's button.
              event.currentTarget.removeAttribute('src');
            }}
          />
        )}
        <div
          className="youtube-pointer-shield"
          aria-hidden="true"
          onContextMenu={(event) => event.preventDefault()}
        />
      </div>
    </VideoCropViewport>
  );
};

export default YouTubePracticePlayer;
